import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { fetchHrUniqueCodeSettings, updateHrUniqueCodeSettings } from "../../../../apis/hrApi";

// entity_type -> display label mapping
const ENTITY_LABELS: Record<string, string> = {
  EMPLOYEE: "Employee",
  DEPARTMENT: "Department",
  DESIGNATION: "Designation",
  LOAN_AND_ADVANCE: "Loan & Advance",
};

// Order to display sections
const ENTITY_ORDER = ["EMPLOYEE", "DEPARTMENT", "DESIGNATION", "LOAN_AND_ADVANCE"];

type CodeSetting = {
  id: string;
  entity_type: string;
  series_code: string;
  connector: string;
  sequence_length: number;
};

export default function UniqueIdentifyCode() {
  const [settings, setSettings] = useState<CodeSetting[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load settings from API on mount
  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchHrUniqueCodeSettings();
      if (res.success) {
        let list: any[] = [];
        if (Array.isArray(res.data)) {
          list = res.data;
        } else if (res.data && typeof res.data === "object") {
          const possible = res.data.list || res.data.data || res.data.settings;
          if (Array.isArray(possible)) {
            list = possible;
          } else {
            const found = Object.values(res.data).find(Array.isArray);
            if (found) list = found as any[];
          }
        }

        // Sort by ENTITY_ORDER
        const sorted = [...list].sort((a, b) => {
          const ai = ENTITY_ORDER.indexOf(a.entity_type);
          const bi = ENTITY_ORDER.indexOf(b.entity_type);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });

        setSettings(
          sorted.map((s: any) => ({
            id: s.id || s._id,
            entity_type: s.entity_type,
            series_code: s.series_code || "",
            connector: s.connector || "-",
            sequence_length: s.sequence_length ?? 4,
          }))
        );
      } else {
        toast.error(res.message || "Failed to load settings");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Unique Identify Code settings");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Update a field in local state
  const handleChange = (idx: number, field: keyof CodeSetting, value: string | number) => {
    setSettings((prev) =>
      prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s))
    );
  };

  // Save all settings — single PATCH with full array payload
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = settings.map((s) => ({
        entity_type: s.entity_type,
        series_code: s.series_code,
        connector: s.connector,
        sequence_length: Number(s.sequence_length),
      }));

      const res = await updateHrUniqueCodeSettings(payload);
      if (res.success) {
        toast.success("Unique Identify Code settings saved successfully!");
        loadSettings();
      } else {
        toast.error(res.message || "Failed to save settings");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error saving settings");
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel — reload from API to discard changes
  const handleCancel = () => {
    loadSettings();
    toast.success("Changes discarded.");
  };

  // Compute preview
  const getPreview = (s: CodeSetting) => {
    const len = Math.max(1, Number(s.sequence_length) || 1);
    const zeros = "0".repeat(len - 1) + "1";
    return `${s.series_code}${s.connector}${zeros}`;
  };

  return (
    <div className="space-y-4 border border-[#d2d2d2] rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] mb-4 mx-auto max-w-5xl mt-2">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-[#212121]">Unique Identify Code Settings</h2>
        {isLoading && (
          <span className="text-xs text-gray-400 font-medium animate-pulse">Loading...</span>
        )}
      </div>

      <div className="bg-[#FFFFFF] p-6 rounded-lg space-y-6 border border-[#D2D2D2]">
        {settings.length === 0 && !isLoading ? (
          <div className="text-center py-8 text-gray-400 text-sm font-medium">
            No settings found. Please check your connection.
          </div>
        ) : (
          settings.map((sec, idx) => (
            <div key={sec.id || idx} className="border border-[#D2D2D2] rounded-xl p-4 relative pt-6 mt-2">
              <h3 className="text-sm font-medium text-[#212121] bg-white px-2 absolute -top-3 left-4">
                {ENTITY_LABELS[sec.entity_type] || sec.entity_type}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Series Code</label>
                  <input
                    type="text"
                    value={sec.series_code}
                    onChange={(e) => handleChange(idx, "series_code", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Connector</label>
                  <input
                    type="text"
                    value={sec.connector}
                    onChange={(e) => handleChange(idx, "connector", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Sequence Length</label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={sec.sequence_length}
                    onChange={(e) => handleChange(idx, "sequence_length", parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-[#FBD037] text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Preview</label>
                  <input
                    type="text"
                    readOnly
                    disabled
                    value={getPreview(sec)}
                    className="w-full px-3 py-2 border border-gray-100 rounded-lg bg-gray-50 text-gray-400 outline-none text-sm"
                  />
                </div>
              </div>
            </div>
          ))
        )}

        <div className="flex justify-end pt-4 gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || isLoading || settings.length === 0}
            className="bg-[#FBD037] text-[#212121] text-sm font-semibold px-5 py-2 rounded-full disabled:opacity-60 disabled:cursor-not-allowed hover:bg-[#e2bd30] transition"
          >
            {isSaving ? "Saving..." : "Save Settings"}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSaving || isLoading}
            className="border border-[#D2D2D2] text-[#212121] text-sm font-semibold px-5 py-2 rounded-full hover:bg-gray-50 transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
